/**
 * Sondos Smart Cart — the only things JS is allowed to do here.
 *
 *   1. announce the reward line in a live region that survives re-renders
 *   2. celebrate a milestone, once, and only when it is actually crossed
 *   3. redeem the threshold discount code, once per cart state
 *   4. keep the checkout dock XOR with the in-flow CTA: never both at once.
 *      The dock is on while the original is outside the usable viewport
 *      (the last 112px is the dock itself and does not count as usable).
 *
 * (3) exists because Shopify never applies a discount CODE by itself — only
 * an "Automatic" discount does that, and this store cannot create one. So
 * when the cart clears the threshold the code is redeemed on the shopper's
 * behalf through /cart/update.js, the same endpoint sondos-cart-promo.js
 * uses. It is never the /discount/<code> URL: that path is what triggers
 * Shopify's "verify you are human" wall.
 *
 * It never builds, reorders or removes cart DOM. `assets/cart.js` replaces
 * `#main-cart-items .js-contents` wholesale after every quantity change, so
 * any node this file held a reference to would be detached a moment later.
 * Instead it re-reads `data-*` off whatever markup Liquid just produced —
 * which also means the page is fully correct with this file blocked.
 *
 * No layout is read anywhere: not in a click handler, not on cart:updated.
 * Class flips are handed to the next frame, so nothing here can land on the
 * INP critical path.
 */
(function () {
  'use strict';

  var STAGE_KEY = 'snd_sc_stage';
  var CODE_KEY = 'snd_sc_code';
  var WIN_MS = 2400;

  // Per-session, not per-page-load. `prevStage === null` on the very first
  // sync of a session is the whole fix for the prototype's phantom
  // celebration: a cart that was already past the threshold before the page
  // opened has not crossed anything, and must stay quiet.
  var lastMessage = null;
  var winTimer = null;
  var stickyObserver = null;

  function readStage() {
    try {
      var raw = window.sessionStorage.getItem(STAGE_KEY);
      return raw === null ? null : parseInt(raw, 10) || 0;
    } catch (e) {
      return null;
    }
  }

  function writeStage(stage) {
    try {
      window.sessionStorage.setItem(STAGE_KEY, String(stage));
    } catch (e) {
      /* private mode / quota — the bar still renders, it just never cheers */
    }
  }

  function celebrate(el) {
    window.requestAnimationFrame(function () {
      el.classList.add('is-won');
    });
    window.clearTimeout(winTimer);
    winTimer = window.setTimeout(function () {
      el.classList.remove('is-won');
    }, WIN_MS);
  }

  function sectionsToRender() {
    var out = [];
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var node = document.getElementById(id);
      if (node && node.dataset.id) out.push({ id: id, section: node.dataset.id });
    });
    return out;
  }

  // Server-rendered HTML back into the same `.js-contents` nodes cart.js
  // owns. Nothing is constructed here — Liquid stays the only author of
  // this markup, so the totals, the bar and the chips cannot disagree.
  // `map` is the Section Rendering API payload: { "<section-id>": "<html>" }.
  function paintSections(map, reqs) {
    if (!map) return false;
    var painted = false;
    reqs.forEach(function (req) {
      var root = document.getElementById(req.id);
      var html = map[req.section];
      if (!root || !html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
      var target = root.querySelector('.js-contents');
      if (!parsed || !target) return;
      target.innerHTML = parsed.innerHTML;
      painted = true;
    });
    return painted;
  }

  /**
   * At most ONE redemption attempt per distinct cart state. This is not an
   * optimisation — Shopify scores repeated discount writes as code probing
   * and answers with the Cloudflare wall on /cart. A code that does not
   * stick must therefore never retry on the next render; a cart that
   * actually changed gets one fresh attempt and no more.
   *
   * The stamp is written BEFORE the request, so a failure or a dropped
   * connection cannot turn into a retry loop either.
   */
  function redeem(el) {
    if (el.getAttribute('data-sc-apply') !== '1') return;
    var code = el.getAttribute('data-sc-code');
    if (!code) return;

    var stamp = code + ':' + (el.getAttribute('data-sc-cart') || '');
    try {
      if (window.sessionStorage.getItem(CODE_KEY) === stamp) return;
      window.sessionStorage.setItem(CODE_KEY, stamp);
    } catch (e) {
      // No sessionStorage means no throttle, and no throttle means a
      // possible retry loop against bot protection. Skip instead.
      return;
    }

    var reqs = sectionsToRender();

    // /discount/<code> is the ONLY endpoint that actually redeems a code.
    //
    // This was measured, not assumed. On one 980 NIS cart, in one session:
    //   POST /cart/update.js {discount:"Sondos"} → applicable:false, 0 off,
    //                                              and not even persisted
    //   GET  /discount/Sondos                    → applicable:true, 68.6 off
    // `discount` is simply not a parameter of the Cart AJAX API; Shopify
    // echoes it back and discards it. Two other files in this theme
    // (sondos-cart-promo.js, ai-routine-builder.js) still call update.js and
    // report success off `response.ok` — they are announcing a discount that
    // never lands. Flagged separately; not fixed here.
    //
    // `?redirect=` keeps the response small and useful: instead of following
    // through to a full HTML page, Shopify hands back cart.js — which doubles
    // as confirmation of whether the code actually applied.
    var root = ((window.routes && window.routes.cart_url) || '/cart').replace(/cart\/?$/, '');
    var applyUrl = root + 'discount/' + encodeURIComponent(code) +
      '?redirect=' + encodeURIComponent(root + 'cart.js');

    fetch(applyUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        // Silent for the shopper — she is never shown an error she cannot act
        // on — but loud in the console, because "applicable: false" is a
        // Discounts-admin problem (inactive code, customer-segment
        // restriction, usage limit, wrong minimum type) that nothing in the
        // theme can fix. Without this, the next person debugging it sees a
        // successful request and a cart that never changes.
        if (cart && cart.discount_codes) {
          [].concat(cart.discount_codes).forEach(function (entry) {
            if (entry && entry.applicable === false) {
              console.warn('[smart-cart] Shopify rejected the discount code — check Discounts in the admin:', entry.code);
            }
          });
        }
        if (!reqs.length) return null;

        // Section Rendering API: Liquid re-renders the totals, the bar and
        // the chips from the cart Shopify now holds, so nothing on screen can
        // disagree with what will be charged.
        return fetch(
          window.location.pathname + '?sections=' + reqs.map(function (r) { return r.section; }).join(','),
          { credentials: 'same-origin', headers: { Accept: 'application/json' } }
        )
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (map) {
            if (paintSections(map, reqs)) sync();
          });
      })
      .catch(function () { /* silent: the cart is unchanged and still correct */ });
  }

  function syncSticky() {
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }

    var sticky = document.querySelector('[data-sondos-cart-sticky]');
    var primary = document.getElementById('checkout');
    if (!sticky || !primary) return;

    if (!('IntersectionObserver' in window)) {
      sticky.classList.add('is-visible');
      primary.setAttribute('inert', '');
      sticky.removeAttribute('inert');
      return;
    }

    stickyObserver = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      window.requestAnimationFrame(function () {
        var showDock = !entry.isIntersecting;
        sticky.classList.toggle('is-visible', showDock);
        if (showDock) {
          primary.setAttribute('inert', '');
          sticky.removeAttribute('inert');
        } else {
          sticky.setAttribute('inert', '');
          primary.removeAttribute('inert');
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -112px 0px' });
    stickyObserver.observe(primary);
  }

  function syncRecoPickers() {
    document.querySelectorAll('[data-sc-reco-picker]').forEach(function (picker) {
      var button = picker.querySelector('[data-sc-reco-swap]');
      var cards = [].slice.call(picker.querySelectorAll('[data-sc-reco-card]'));
      if (!button || cards.length < 2 || button.dataset.scReady === '1') return;
      button.dataset.scReady = '1';
      button.addEventListener('click', function () {
        var current = cards.findIndex(function (card) { return !card.hidden; });
        var next = current < 0 ? 0 : (current + 1) % cards.length;
        cards.forEach(function (card, index) { card.hidden = index !== next; });
      });
    });
  }

  function syncCartHelp(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    var link = document.querySelector('[data-cart-help-link]');
    if (!link) return;
    var phone = link.getAttribute('data-cart-help-phone') || '';
    var intro = link.getAttribute('data-cart-help-intro') || '';
    if (!phone || !intro) return;
    var lines = cart.items.map(function (item) {
      var title = item.product_title || item.title || '';
      var variant = item.variant_title && item.variant_title !== 'Default Title'
        ? ' — ' + item.variant_title
        : '';
      var quantity = Number(item.quantity) > 1 ? ' × ' + item.quantity : '';
      return '• ' + title + variant + quantity;
    });
    link.href = 'https://wa.me/' + phone + '?text=' + encodeURIComponent([intro].concat(lines).join('\n'));
  }

  function sync(event) {
    syncSticky();
    syncRecoPickers();
    syncCartHelp(event && event.detail && event.detail.cart);
    var el = document.querySelector('[data-sondos-sc-reward]');

    // Cart emptied, or the merchant switched the bar off between renders.
    // Drop the stored stage so the next cart starts from a clean slate
    // rather than inheriting a milestone it never reached.
    if (!el) {
      try {
        window.sessionStorage.removeItem(STAGE_KEY);
      } catch (e) {}
      lastMessage = null;
      return;
    }

    var stage = parseInt(el.getAttribute('data-sc-stage'), 10) || 0;
    var message = el.getAttribute('data-sc-announce') || '';
    var prevStage = readStage();

    // Only announce a CHANGE. The first sync is silent because the same
    // sentence is already on screen and in the reading order.
    if (lastMessage !== null && message && message !== lastMessage) {
      var live = document.getElementById('sondos-sc-live');
      if (live) live.textContent = message;
    }
    lastMessage = message;

    if (prevStage !== null && stage > prevStage) celebrate(el);
    writeStage(stage);

    redeem(el);
  }

  document.addEventListener('cart:updated', sync);
  sync();
})();
